import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useEffect } from 'react';
import { queryKeys } from '~/shared/api/queryKeys';
import {
    adminPayinOrderServiceApproveOrder,
    adminPayinOrderServiceDeclineOrder,
    adminPayinOrderServiceGetOrderChecks,
    adminPayinOrderServiceGetOrderEvents,
    adminPayinOrderServiceListOrders,
    getAdminPayinOrderServiceGetOrderChecksUrl,
    getAdminPayinOrderServiceGetOrderEventsUrl,
    getAdminPayinOrderServiceListOrdersUrl,
} from '~/generated/payinBackend/order';
import {
    type PayinAdminOrderV1DeclineOrderRequest,
    type AdminPayinOrderServiceListOrdersParams,
    type AdminPayinOrderServiceGetOrderChecksParams,
    type AdminPayinOrderServiceGetOrderEventsParams,
    type PayinAdminOrderV1ApproveOrderRequest,
} from '~/generated/payinBackend/model';
import { type Filters } from '../model';

type QueryOptions<Data> = Omit<UseQueryOptions<Data, Error, Data>, 'queryFn' | 'queryKey'>;

type OrdersParams = Omit<
    AdminPayinOrderServiceListOrdersParams,
    'filter.currency' | 'filter.publicStatuses' | 'filter.statuses'
> & {
    'filter.currency'?: Filters['currency'];
    'filter.publicStatuses'?: Filters['publicStatuses'];
    'filter.statuses'?: Filters['statuses'];
};

const toGeneratedOrdersParams = (params?: OrdersParams): AdminPayinOrderServiceListOrdersParams | undefined => {
    return params as AdminPayinOrderServiceListOrdersParams | undefined;
};

const invalidateOrderQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
    return Promise.all([
        queryClient.invalidateQueries({
            queryKey: [queryKeys.PAYIN_ORDERS],
        }),
        queryClient.invalidateQueries({
            queryKey: [queryKeys.PAYIN_ORDER],
        }),
    ]);
};

export const useOrdersList = (params?: OrdersParams, refetchInterval?: number | false) => {
    const generatedParams = toGeneratedOrdersParams(params);

    return useQuery({
        refetchInterval,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayinOrderServiceListOrders(generatedParams),
        queryKey: [queryKeys.PAYIN_ORDERS, getAdminPayinOrderServiceListOrdersUrl(generatedParams)],
    });
};

interface UseOrderMutationProps {
    onError?: (error: Error) => void;
    onSuccess?: () => void;
}

export const useDeclineOrder = (options?: UseOrderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinAdminOrderV1DeclineOrderRequest) => {
            return await adminPayinOrderServiceDeclineOrder(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            invalidateOrderQueries(queryClient).then(() => {
                options?.onSuccess?.();
                notifications.show({
                    color: 'green',
                    title: 'Успешно',
                    message: 'Ордер удален',
                });
            });
        },
    });
};

export const useApproveOrder = (options?: UseOrderMutationProps) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: PayinAdminOrderV1ApproveOrderRequest) => {
            return await adminPayinOrderServiceApproveOrder(payload);
        },
        onError: options?.onError,
        onSuccess: () => {
            invalidateOrderQueries(queryClient).then(() => {
                options?.onSuccess?.();
                notifications.show({
                    color: 'green',
                    title: 'Успешно',
                    message: 'Ордер удален',
                });
            });
        },
    });
};

type OrderChecksResponse = Awaited<ReturnType<typeof adminPayinOrderServiceGetOrderChecks>>;
type OrderChecksQueryOptions<Data> = QueryOptions<Data> & {
    onError?: VoidFunction;
};

export const useOrderChecks = (
    params: AdminPayinOrderServiceGetOrderChecksParams,
    options: OrderChecksQueryOptions<OrderChecksResponse> = {},
) => {
    const { onError, ...queryOptions } = options;

    const queryResult = useQuery<OrderChecksResponse>({
        queryFn: () =>
            adminPayinOrderServiceGetOrderChecks(params, {
                context: {
                    skipErrorNotification: true,
                },
            }),
        queryKey: [getAdminPayinOrderServiceGetOrderChecksUrl(params)],
        ...queryOptions,
    });

    useEffect(() => {
        if (!queryResult.data) {
            onError?.();
        }
    }, [onError, queryResult.data]);

    return queryResult;
};

export const useOrderEvents = (params: AdminPayinOrderServiceGetOrderEventsParams) => {
    return useQuery({
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        queryFn: () => adminPayinOrderServiceGetOrderEvents(params),
        queryKey: [queryKeys.PAYIN_ORDERS, getAdminPayinOrderServiceGetOrderEventsUrl(params)],
    });
};
